/**
 * Phase 13 Metrics Dashboard — Manager-only operational surface for the
 * DVI learning system. Answers: Is the system learning? Correctly?
 * What's ready to advance?
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useOptiDvi } from '@/hooks/useOptiDvi';
import { useFeatureStore } from '@/stores/featureStore';
import { useDviLearningDashboard } from '@/hooks/useDviLearningDashboard';
import { useDviStageReadiness, type StageReadinessRow } from '@/hooks/useDviStageReadiness';
import { LearningFilterBar, type LearningFilters } from '@/components/dvi/learning/LearningFilterBar';
import { GlobalLearningSummary } from '@/components/dvi/learning/GlobalLearningSummary';
import { ComponentConfidenceTable } from '@/components/dvi/learning/ComponentConfidenceTable';
import { ComponentConfidenceDetailDrawer } from '@/components/dvi/learning/ComponentConfidenceDetailDrawer';
import { StageReadinessPanel } from '@/components/dvi/learning/StageReadinessPanel';
import { OcrEffectivenessPanel } from '@/components/dvi/learning/OcrEffectivenessPanel';
import { AcceptanceBreakdownPanel } from '@/components/dvi/learning/AcceptanceBreakdownPanel';
import { WeakestComponentsPanel } from '@/components/dvi/learning/WeakestComponentsPanel';
import { BestPerformersPanel } from '@/components/dvi/learning/BestPerformersPanel';
import { LearningEventTimeline } from '@/components/dvi/learning/LearningEventTimeline';
import { PromotionHistoryPanel } from '@/components/dvi/learning/PromotionHistoryPanel';
import { ComponentMaturityMap } from '@/components/dvi/learning/ComponentMaturityMap';

export default function DviLearningMetricsDashboard() {
  const { activeShop } = useAuth();
  const permissions = usePermissions();
  const optiDvi = useOptiDvi();
  const flags = useFeatureStore((s) => s.featureFlags);

  const isManager = permissions.isManager || permissions.isOwner;
  const flagOn = flags?.ui_dviLearning ?? true;

  const [filters, setFilters] = useState<LearningFilters>({
    rangeDays: 30,
    stage: 'all',
    recommendation: null,
    ocrBand: null,
    minSamples: 0,
    search: '',
  });
  const [selected, setSelected] = useState<StageReadinessRow | null>(null);

  const shopId = activeShop?.id ?? null;
  const { data: summary, isLoading: summaryLoading } = useDviLearningDashboard(
    shopId, filters.rangeDays, filters.recommendation, filters.ocrBand,
  );
  const { data: rows = [], isError: readinessUnavailable } = useDviStageReadiness(shopId, filters.recommendation, filters.ocrBand);

  if (!isManager || !optiDvi.isEnabled || !flagOn) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">DVI Learning Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          Watch confidence grow, identify what's ready to advance, and detect regressions.
        </p>
      </div>

      <LearningFilterBar value={filters} onChange={setFilters} />

      <GlobalLearningSummary data={summary} isLoading={summaryLoading} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StageReadinessPanel rows={rows} onSelect={setSelected} unavailable={readinessUnavailable} />
        <LearningEventTimeline shopId={shopId} />
      </div>

      <ComponentConfidenceTable
        rows={rows}
        onSelect={setSelected}
        stageFilter={filters.stage}
        minSamples={filters.minSamples}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <WeakestComponentsPanel rows={rows} onSelect={setSelected} />
        <BestPerformersPanel rows={rows} onSelect={setSelected} />
      </div>

      <OcrEffectivenessPanel summary={summary} rows={rows} />

      <AcceptanceBreakdownPanel summary={summary} />

      {/* Phase 15 — Promotion engine surfaces */}
      <ComponentMaturityMap shopId={shopId} />
      <PromotionHistoryPanel shopId={shopId} canRevert={isManager} />

      <ComponentConfidenceDetailDrawer row={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
